import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  User as UserIcon,
  ShieldCheck,
  Trash,
  WarningOctagon,
  ArrowRight,
  Buildings,
  Wrench,
  Stack,
  PencilSimple,
  Check,
  X,
} from "@phosphor-icons/react";

const SECTOR_OPTIONS = [
  { id: "costruzioni", label: "Costruzioni", icon: Buildings },
  { id: "impiantistica", label: "Impiantistica", icon: Wrench },
  { id: "entrambi", label: "Costruzioni e Impiantistica", icon: Stack },
];

const SECTOR_LABELS = {
  costruzioni: "Costruzioni",
  impiantistica: "Impiantistica",
  entrambi: "Costruzioni e Impiantistica",
  edile: "Costruzioni",
  elettrico: "Impiantistica",
  idraulico: "Impiantistica",
  architetto: "Costruzioni",
  ingegnere: "Costruzioni",
};

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  // Editable company data
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    sector: user?.sector || "costruzioni",
    company_name: user?.company_name || "",
    vat: user?.vat || "",
    billing_address: user?.billing_address || "",
    billing_city: user?.billing_city || "",
    billing_zip: user?.billing_zip || "",
    billing_country: user?.billing_country || "Italia",
  });
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setForm({
      sector: user?.sector || "costruzioni",
      company_name: user?.company_name || "",
      vat: user?.vat || "",
      billing_address: user?.billing_address || "",
      billing_city: user?.billing_city || "",
      billing_zip: user?.billing_zip || "",
      billing_country: user?.billing_country || "Italia",
    });
    setEditing(true);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/users/profile", form);
      await refresh();
      toast.success("Profilo aggiornato");
      setEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== "ELIMINA") {
      toast.error("Scrivi ELIMINA per confermare");
      return;
    }
    setBusy(true);
    try {
      await api.delete("/users/me");
      toast.success("Account eliminato definitivamente");
      logout();
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore eliminazione account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold">Account</div>
        <h1 className="font-heading text-4xl font-black uppercase tracking-tighter leading-none mt-1">
          Profilo
        </h1>
      </div>

      <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-4 mb-4" data-testid="profile-info">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#FFD600] rounded-md flex items-center justify-center">
              <UserIcon size={28} weight="fill" className="text-[#0A0A0A]" />
            </div>
            <div>
              <div className="font-heading text-xl font-black uppercase leading-none">
                {user?.name || "—"}
              </div>
              <div className="text-xs text-[#A3A3A3] mt-1">{user?.email}</div>
            </div>
          </div>
          {!editing && (
            <button
              data-testid="profile-edit-btn"
              onClick={startEdit}
              className="text-xs font-bold uppercase tracking-wider px-3 py-2 rounded bg-[#FFD600]/10 text-[#FFD600] border border-[#FFD600]/30 hover:bg-[#FFD600] hover:text-[#0A0A0A] transition flex items-center gap-1"
            >
              <PencilSimple size={14} weight="bold" /> Modifica
            </button>
          )}
        </div>

        {!editing ? (
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between border-t border-[#333] pt-2">
              <dt className="text-[#A3A3A3] uppercase tracking-wider text-xs font-bold">Settore</dt>
              <dd className="font-bold">{SECTOR_LABELS[user?.sector] || "—"}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-[#A3A3A3] uppercase tracking-wider text-xs font-bold">Azienda</dt>
              <dd className="font-bold">{user?.company_name || "—"}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-[#A3A3A3] uppercase tracking-wider text-xs font-bold">P.IVA</dt>
              <dd className="font-mono">{user?.vat || "—"}</dd>
            </div>
            {(user?.billing_address || user?.billing_city) && (
              <div className="flex justify-between">
                <dt className="text-[#A3A3A3] uppercase tracking-wider text-xs font-bold">Indirizzo</dt>
                <dd className="text-right">
                  {user?.billing_address}
                  {user?.billing_address && (user?.billing_city || user?.billing_zip) && <br />}
                  {[user?.billing_zip, user?.billing_city].filter(Boolean).join(" ")}
                </dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt className="text-[#A3A3A3] uppercase tracking-wider text-xs font-bold">Stato</dt>
              <dd className="font-bold uppercase text-[#FFD600]">{user?.subscription_status || "—"}</dd>
            </div>
          </dl>
        ) : (
          <div className="space-y-4 border-t border-[#333] pt-4" data-testid="profile-edit-form">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-2 block">
                Settore di attività
              </label>
              <div className="grid grid-cols-1 gap-2">
                {SECTOR_OPTIONS.map((s) => {
                  const Icon = s.icon;
                  const active = form.sector === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      data-testid={`profile-sector-${s.id}`}
                      onClick={() => setForm({ ...form, sector: s.id })}
                      className={`p-3 rounded-md border-2 flex items-center gap-3 text-left transition ${active ? "border-[#FFD600] bg-[#FFD600]/5" : "border-[#333] bg-[#0A0A0A] hover:border-[#666]"}`}
                    >
                      <div className={`w-10 h-10 rounded-md flex items-center justify-center ${active ? "bg-[#FFD600] text-[#0A0A0A]" : "bg-[#1A1A1A] text-[#FFD600]"}`}>
                        <Icon size={22} weight="fill" />
                      </div>
                      <div className="font-bold uppercase text-sm">{s.label}</div>
                      {active && <Check size={20} weight="bold" className="text-[#FFD600] ml-auto" />}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-1 block">
                Nome azienda
              </label>
              <input
                data-testid="profile-company-input"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                placeholder="Es. Rossi Costruzioni Srl"
                className="w-full bg-[#0A0A0A] border-2 border-[#333] h-11 rounded-md px-3 text-white focus:border-[#FFD600] focus:outline-none"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-1 block">
                P.IVA / Cod. Fiscale
              </label>
              <input
                data-testid="profile-vat-input"
                value={form.vat}
                onChange={(e) => setForm({ ...form, vat: e.target.value })}
                placeholder="IT01234567890"
                className="w-full bg-[#0A0A0A] border-2 border-[#333] h-11 rounded-md px-3 text-white focus:border-[#FFD600] focus:outline-none font-mono"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-1 block">
                Indirizzo
              </label>
              <input
                data-testid="profile-address-input"
                value={form.billing_address}
                onChange={(e) => setForm({ ...form, billing_address: e.target.value })}
                placeholder="Via Roma 1"
                className="w-full bg-[#0A0A0A] border-2 border-[#333] h-11 rounded-md px-3 text-white focus:border-[#FFD600] focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-1 block">CAP</label>
                <input
                  data-testid="profile-zip-input"
                  value={form.billing_zip}
                  onChange={(e) => setForm({ ...form, billing_zip: e.target.value })}
                  placeholder="20100"
                  className="w-full bg-[#0A0A0A] border-2 border-[#333] h-11 rounded-md px-3 text-white focus:border-[#FFD600] focus:outline-none font-mono"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-1 block">Città</label>
                <input
                  data-testid="profile-city-input"
                  value={form.billing_city}
                  onChange={(e) => setForm({ ...form, billing_city: e.target.value })}
                  placeholder="Milano"
                  className="w-full bg-[#0A0A0A] border-2 border-[#333] h-11 rounded-md px-3 text-white focus:border-[#FFD600] focus:outline-none"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                data-testid="profile-cancel-btn"
                onClick={() => setEditing(false)}
                disabled={saving}
                className="flex-1 h-12 rounded-md bg-[#1A1A1A] border-2 border-[#333] text-white font-black uppercase text-sm hover:border-[#666] transition flex items-center justify-center gap-2"
              >
                <X size={16} weight="bold" /> Annulla
              </button>
              <button
                type="button"
                data-testid="profile-save-btn"
                onClick={saveProfile}
                disabled={saving}
                className="flex-1 h-12 rounded-md bg-[#FFD600] text-[#0A0A0A] font-black uppercase text-sm hover:bg-[#E6C200] transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Check size={16} weight="bold" /> {saving ? "Salvo…" : "Salva"}
              </button>
            </div>
          </div>
        )}
      </div>

      <Link
        to="/referral"
        data-testid="goto-referral"
        className="bg-[#1A1A1A] border-2 border-[#FFD600]/30 rounded-md p-4 mb-4 flex items-center justify-between hover:border-[#FFD600] transition"
      >
        <div className="flex items-center gap-3">
          <UserIcon size={24} weight="fill" className="text-[#FFD600]" />
          <div>
            <div className="font-bold uppercase text-sm text-[#FFD600]">Invita amici · Guadagna sconti</div>
            <div className="text-xs text-[#A3A3A3]">50% di sconto per 2 mesi per te e per loro</div>
          </div>
        </div>
        <ArrowRight size={20} weight="bold" className="text-[#FFD600]" />
      </Link>

      <Link
        to="/privacy"
        data-testid="goto-privacy"
        className="bg-[#1A1A1A] border border-[#333] rounded-md p-4 mb-4 flex items-center justify-between hover:border-[#FFD600] transition"
      >
        <div className="flex items-center gap-3">
          <ShieldCheck size={24} weight="bold" className="text-[#FFD600]" />
          <div>
            <div className="font-bold uppercase text-sm">Privacy Policy & GDPR</div>
            <div className="text-xs text-[#A3A3A3]">Informativa sul trattamento dei dati</div>
          </div>
        </div>
        <ArrowRight size={20} weight="bold" className="text-[#A3A3A3]" />
      </Link>

      <Link
        to="/termini"
        data-testid="goto-terms"
        className="bg-[#1A1A1A] border border-[#333] rounded-md p-4 mb-4 flex items-center justify-between hover:border-[#FFD600] transition"
      >
        <div className="flex items-center gap-3">
          <ShieldCheck size={24} weight="bold" className="text-[#FFD600]" />
          <div>
            <div className="font-bold uppercase text-sm">Termini & Condizioni</div>
            <div className="text-xs text-[#A3A3A3]">Abbonamento, pagamenti, limitazioni</div>
          </div>
        </div>
        <ArrowRight size={20} weight="bold" className="text-[#A3A3A3]" />
      </Link>

      {user?.is_admin && (
        <Link
          to="/admin"
          data-testid="goto-admin"
          className="bg-[#1A1A1A] border-2 border-[#FFD600]/30 rounded-md p-4 mb-4 flex items-center justify-between hover:border-[#FFD600] transition"
        >
          <div className="flex items-center gap-3">
            <ShieldCheck size={24} weight="fill" className="text-[#FFD600]" />
            <div>
              <div className="font-bold uppercase text-sm text-[#FFD600]">Console Admin</div>
              <div className="text-xs text-[#A3A3A3]">MRR, abbonamenti, codici sconto</div>
            </div>
          </div>
          <ArrowRight size={20} weight="bold" className="text-[#FFD600]" />
        </Link>
      )}

      {/* Danger zone */}
      {!user?.is_admin && (
        <div className="bg-[#1A1A1A] border-2 border-[#FF3B30]/40 rounded-md p-4 mb-4" data-testid="danger-zone">
          <div className="flex items-center gap-2 mb-3">
            <WarningOctagon size={22} weight="fill" className="text-[#FF3B30]" />
            <h2 className="font-heading font-black uppercase text-base text-[#FF3B30]">Zona pericolo</h2>
          </div>
          <p className="text-xs text-[#A3A3A3] leading-snug mb-3">
            L'eliminazione dell'account è <b>definitiva e irreversibile</b>. Saranno cancellati per sempre:
            preventivi, foto, timbrature, dipendenti, attestati, listini personalizzati. Conforme al GDPR art. 17.
          </p>

          {!confirmOpen ? (
            <button
              data-testid="delete-account-btn"
              onClick={() => setConfirmOpen(true)}
              className="w-full bg-transparent border-2 border-[#FF3B30] text-[#FF3B30] h-12 rounded-md font-black uppercase text-sm hover:bg-[#FF3B30] hover:text-white transition flex items-center justify-center gap-2"
            >
              <Trash size={18} weight="bold" /> Elimina account
            </button>
          ) : (
            <div className="space-y-3">
              <label className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold block">
                Scrivi <span className="text-[#FF3B30]">ELIMINA</span> per confermare
              </label>
              <input
                data-testid="delete-confirm-input"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="ELIMINA"
                className="w-full bg-[#0A0A0A] border-2 border-[#FF3B30]/40 h-12 rounded-md px-3 focus:border-[#FF3B30] focus:outline-none uppercase tracking-wider font-mono"
              />
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => { setConfirmOpen(false); setConfirmText(""); }}
                  disabled={busy}
                  data-testid="delete-cancel-btn"
                  className="bg-[#1A1A1A] border border-[#333] text-white h-12 rounded-md font-black uppercase text-sm hover:border-[#FFD600]"
                >
                  Annulla
                </button>
                <button
                  data-testid="delete-confirm-btn"
                  onClick={handleDelete}
                  disabled={busy || confirmText !== "ELIMINA"}
                  className="bg-[#FF3B30] text-white h-12 rounded-md font-black uppercase text-sm hover:bg-[#D32F2F] transition disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  <Trash size={16} weight="bold" /> {busy ? "Elimino…" : "Conferma"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {user?.is_admin && (
        <div className="bg-[#FFD600]/10 border border-[#FFD600]/30 rounded-md p-3 text-xs text-[#FFD600] uppercase tracking-wider font-bold">
          Account amministratore — protetto da eliminazione
        </div>
      )}
    </Layout>
  );
}
